import {
  getSingleValueForTasField,
  setTrackedTasFieldValue,
} from '../../utils/tuition-assistance/fields';

import type { TasAssistanceExtension as TasAssistanceModel } from 'tio-data/data/resources/tas-assistance.ext';
import type {
  ApplicationApprovalSummaryApprover,
  TasApplicationState,
  TasApplicationPaymentState,
  TasClaimsFinancials,
  AssistanceAppHistory,
  AppHistoryBase,
  ReceivedScholarship,
} from '../../types/tuition-assistance';
import { createDefaultClaimsFinancialsWithBenefitPercent } from '../../utils/tuition-assistance/claims-financials-calculator';
import ClaimsFinancialsCalculator from '../../utils/tuition-assistance/claims-financials-calculator';
import type { ChipSignature } from 'tio-ui/components/chip';
import type { TasApplication } from './tas-application.type.ts';
import { tasStateLabelKeyMap, tasStateLabelMap } from './tas-application.constants';

const programTypesWhereAttendIsDraft = new Set(['TAS.ProgramType.2', 'TAS.ProgramType.4.2']);

type IntentKey = string;

const formatStateKey = (
  state: TasApplicationState | TasApplicationPaymentState,
  fromState?: TasApplicationState,
  typeClassification?: string
): string => {
  if (state === 'TAS.ApplicationState.ATTEND') {
    if (typeClassification && programTypesWhereAttendIsDraft.has(typeClassification)) {
      // behave exactly like DEFINE_COURSES / "In Draft"
      return 'TAS.ApplicationState.DEFINE_COURSES';
    }

    // Only the pre-approval bounceback reads differently. Every other route into
    // ATTEND is "attending, evidence outstanding" — including a migrated record
    // whose transitionLog is empty, leaving no fromState to match on.
    return fromState === 'TAS.ApplicationState.COURSES_NOT_APPROVED'
      ? 'CHANGES_REQUESTED'
      : 'IN_PROGRESS';
  }

  return state;
};

const tasIntentMap: Record<IntentKey, ChipSignature['Args']['intent']> = {
  // application states
  'TAS.ApplicationState.CREATED': 'primary',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL': 'primary',
  'TAS.ApplicationState.FULFILLED': 'success',
  'TAS.ApplicationState.ATTEND': 'success',
  'TAS.ApplicationState.COURSES_NOT_APPROVED': 'warning',
  CHANGES_REQUESTED: 'default',
  IN_PROGRESS: 'success',
  CREATED: 'primary',
  DRAFT: 'primary', // treat draft similarly to created
  'TAS.ApplicationState.DEFINE_COURSES': 'default',
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL': 'primary',
  'TAS.ApplicationState.COURSES_APPROVED': 'success',
  'TAS.ApplicationState.COURSES_REJECTED': 'danger',
  'TAS.ApplicationState.EVIDENCE_APPROVED': 'success',
  'TAS.ApplicationState.EVIDENCE_NOT_APPROVED': 'danger',
  'TAS.ApplicationState.EVIDENCE_REJECTED': 'danger',
  'TAS.ApplicationState.REJECTED_PER_POLICY': 'danger',
  'TAS.ApplicationState.ABANDONED': 'warning',
  // payment states
  'TAS.ApplicationPaymentState.NONE': 'default',
  'TAS.ApplicationPaymentState.UNPAID': 'default',
  'TAS.ApplicationPaymentState.PAID_IN_PART': 'success',
  'TAS.ApplicationPaymentState.PAID_IN_FULL': 'success',
  'TAS.ApplicationPaymentState.REFUND_REQUESTED': 'danger',
};

const firstTimeSubmissionMap: Record<string, string> = {
  'TAS.ProgramType.2': 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
  'TAS.ProgramType.4.2': 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
  'TAS.ProgramType.3': 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ProgramType.4.3': 'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
};

const firstApprovalDateMap: Record<string, string> = {
  'TAS.ProgramType.2': 'TAS.ApplicationState.EVIDENCE_APPROVED',
  'TAS.ProgramType.4.2': 'TAS.ApplicationState.EVIDENCE_APPROVED',
  'TAS.ProgramType.3': 'TAS.ApplicationState.COURSES_APPROVED',
  'TAS.ProgramType.4.3': 'TAS.ApplicationState.COURSES_APPROVED',
};

/**
 * Format a created-at value as "Month YYYY" via native Intl, guarding the
 * invalid-date case: `Intl.DateTimeFormat.format` throws `RangeError: Invalid
 * time value` on an Invalid Date (ember-intl's `formatDate` tolerated it). Only
 * `string | number | Date` are accepted; any other type (and any value `new
 * Date(...)` would coerce to an Invalid Date) degrades to '' so the display
 * getters return empty instead of crashing the render. Note that without the
 * type guard, values like `true` coerce to a VALID date (`new Date(true)` →
 * epoch+1ms) and would otherwise format as "January 1970".
 */
function formatCreatedAtMonthYear(createdAt: unknown): string {
  if (
    typeof createdAt !== 'string' &&
    typeof createdAt !== 'number' &&
    !(createdAt instanceof Date)
  ) {
    return '';
  }
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { month: 'long', year: 'numeric' }).format(date);
}

export interface TasApplicationExtension extends TasApplication {}
export class TasApplicationExtension {
  recalculateClaimsFinancialsAfterAdjacentUpdate(): TasClaimsFinancials {
    const calculator = new ClaimsFinancialsCalculator(this.claimsFinancialsCalculatorInput);
    const results = calculator.results;
    setTrackedTasFieldValue(this, 'CLAIMS_FINANCIALS', results);
    return results;
  }

  get preAttendStates(): Set<TasApplicationState> {
    return new Set([
      'TAS.ApplicationState.DEFINE_COURSES',
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.COURSES_NOT_APPROVED',
      'TAS.ApplicationState.COURSES_REJECTED',
    ]);
  }

  get preAttendAndAttendStates(): Set<TasApplicationState> {
    return new Set([...this.preAttendStates, 'TAS.ApplicationState.ATTEND']);
  }

  get dormantStates(): Set<TasApplicationState> {
    return new Set([
      'TAS.ApplicationState.COURSES_REJECTED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
      'TAS.ApplicationState.ABANDONED',
    ]);
  }

  get canDeleteAppStates(): Set<TasApplicationState> {
    return new Set([
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      'TAS.ApplicationState.DEFINE_COURSES',
    ]);
  }

  get clawbackablePaymentStates() {
    return new Set([
      'TAS.ApplicationPaymentState.REFUND_REQUESTED',
      'TAS.ApplicationPaymentState.PAID_IN_FULL',
      'TAS.ApplicationPaymentState.PAID_IN_PART',
    ]);
  }

  get uneditableApprovedTotalPaymentStates() {
    return new Set([
      'TAS.ApplicationPaymentState.REFUND_REQUESTED',
      'TAS.ApplicationPaymentState.PAID_IN_FULL',
    ]);
  }

  get uneditableApprovedTotalApplicationStates(): Set<TasApplicationState> {
    return new Set([
      'TAS.ApplicationState.COURSES_REJECTED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
      'TAS.ApplicationState.ABANDONED',
    ]);
  }

  get postSubmitForPaymentStates(): Set<TasApplicationState> {
    return new Set([
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      'TAS.ApplicationState.EVIDENCE_APPROVED',
      'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.ABANDONED',
    ]);
  }

  get hasSubmittedApplicationStates(): Set<TasApplicationState> {
    return new Set([
      ...this.dormantStates,
      ...this.uneditableApprovedTotalApplicationStates,
      ...this.postSubmitForPaymentStates,
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.ATTEND',
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.COURSES_NOT_APPROVED',
      'TAS.ApplicationState.COURSES_REJECTED',
    ]);
  }

  get approvedStates(): Set<TasApplicationState> {
    return new Set([
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      'TAS.ApplicationState.ATTEND',
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.EVIDENCE_APPROVED',
    ]);
  }

  get paidStates(): Set<string> {
    return new Set([
      'TAS.ApplicationPaymentState.PAID_IN_FULL',
      'TAS.ApplicationPaymentState.PAID_IN_PART',
    ]);
  }

  get approverCanEditStates(): Set<TasApplicationState> {
    return new Set([
      'TAS.ApplicationState.ATTEND',
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.COURSES_NOT_APPROVED',
      'TAS.ApplicationState.COURSES_REJECTED',
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      'TAS.ApplicationState.EVIDENCE_APPROVED',
      'TAS.ApplicationState.EVIDENCE_NOT_APPROVED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
    ]);
  }

  get restingStates(): Set<TasApplicationState> {
    return new Set([
      'TAS.ApplicationState.ABANDONED',
      'TAS.ApplicationState.FULFILLED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      'TAS.ApplicationState.ATTEND',
      'TAS.ApplicationState.COURSES_REJECTED',
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.DEFINE_COURSES',
    ]);
  }

  get rescindableStates(): Set<TasApplicationState> {
    return new Set([
      'TAS.ApplicationState.DEFINE_COURSES',
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.ATTEND',
      'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
      'TAS.ApplicationState.FULFILLED',
    ]);
  }

  // ── Computed Properties ──
  get activeCourses() {
    return this.tasCourses.filter((course) => course.isActive);
  }

  get applicant(): string {
    return getSingleValueForTasField('NAME', this.tasProgramInstance.fields);
  }

  get applicationName(): string {
    return getSingleValueForTasField('APPLICATION_NAME', this.fields);
  }

  get displayName() {
    return (
      this.applicationName ??
      getSingleValueForTasField('PROGRAM_SPECIALIZATION', this.tasProgramInstance?.fields) ??
      getSingleValueForTasField('PROGRAM_MAJOR', this.tasProgramInstance?.fields) ??
      this.tasProgramInstance?.tasProgramTemplate?.programName ??
      formatCreatedAtMonthYear(this.createdAt)
    );
  }

  get coursesBeginDate(): string {
    return getSingleValueForTasField('COURSES_BEGIN_DATE', this.fields);
  }

  get expenseType(): string {
    return getSingleValueForTasField('EXPENSE_TYPE', this.fields);
  }

  get gradePointAverage(): number {
    return getSingleValueForTasField('GRADE_POINT_AVERAGE', this.fields);
  }

  get isDraft() {
    return this.state === 'TAS.ApplicationState.DEFINE_COURSES';
  }

  get isApproved() {
    return this.approvedStates.has(this.state);
  }

  get netPaidAmount(): number {
    const { paid, refund_requested, refund_forgiven, refund_paid_back } = this.amounts;
    if (refund_requested > 0) {
      return -(refund_requested - refund_forgiven - refund_paid_back);
    } else {
      return paid;
    }
  }

  get isPaid() {
    return this.tasAssistances.some(
      (assistance) => assistance.state === 'TAS.AssistanceState.SETTLED'
    );
  }

  get isRepayable() {
    return this.tasAssistances.some(
      (assistance) => assistance.state === 'TAS.AssistanceState.REPAYABLE'
    );
  }

  get canCancelApplication() {
    const allowedStates = this.canDeleteAppStates;
    if (!this.isPrepaidProgram) allowedStates.add('TAS.ApplicationState.ATTEND');

    return allowedStates.has(this.state);
  }

  get canSubmitEvidence() {
    const courseApprovalRequired =
      this.tasProgramInstance?.tasProgramTemplate?.courseApprovalRequired;

    const validStates = ['TAS.ApplicationState.ATTEND', 'TAS.ApplicationState.COURSES_APPROVED'];

    if (!courseApprovalRequired) {
      validStates.push('TAS.ApplicationState.PENDING_COURSES_APPROVAL');
    }

    return validStates.includes(this.state);
  }

  get canSubmitForPayment() {
    const courseApprovalRequired =
      this.tasProgramInstance?.tasProgramTemplate?.courseApprovalRequired;

    const validStates = ['TAS.ApplicationState.ATTEND', 'TAS.ApplicationState.COURSES_APPROVED'];

    if (!courseApprovalRequired) {
      validStates.push('TAS.ApplicationState.PENDING_COURSES_APPROVAL');
    }

    return validStates.includes(this.state);
  }

  get approverCanEditApplication() {
    return this.approverCanEditStates.has(this.state);
  }

  get fieldForEditableApprovedTotal() {
    if (this.state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return 'PRE_APPROVED_TOTAL';
    }
    if (this.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL') {
      return 'FINAL_APPROVED_TOTAL';
    }
    return 'FINAL_APPROVED_TOTAL';
  }

  get cardPreApprovedTotal() {
    return getSingleValueForTasField('CARD_PRE_APPROVED_TOTAL', this.fields) || 0;
  }

  get cardFinalApprovedTotal() {
    return getSingleValueForTasField('CARD_FINAL_APPROVED_TOTAL', this.fields);
  }

  get isCourseGradeRequired() {
    return this.tasProgramInstance?.tasProgramTemplate?.courseGradeRequired;
  }

  get isMissingCourseGrades() {
    if (this.activeCourses.length === 0) {
      return false;
    }
    return !this.activeCourses.every((course) => course.hasGrades);
  }

  get isEvidenceRequired() {
    if (getSingleValueForTasField('WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT', this.fields) === true) {
      return false;
    }

    return this.tasProgramInstance?.tasProgramTemplate?.requireAttachmentsForCourseCompletion;
  }

  get isPreApprovalAttachmentsRequired() {
    return this.tasProgramInstance?.tasProgramTemplate?.requireAttachmentsForCourseApproval;
  }

  get isPrepaidProgram() {
    return this.tasProgramInstance?.tasProgramTemplate?.programPaymentType === 'ON_ATTEND';
  }

  get paymentMethod() {
    return (
      getSingleValueForTasField('PAYMENT_TYPE_FOR_DIRECT_PAY', this.fields) ||
      'TAS.AssistanceManner.REIMBURSEMENT'
    );
  }

  get evidenceAssets() {
    // evidenceAssets for TYPE 2 programs are stored at the application level
    return Object.freeze(
      this.tasAssets.filter(
        (asset) =>
          !asset.attachmentDeletedAt &&
          (asset.classification === 'COMPLETION_DOCUMENTS' ||
            asset.classification === 'APPLICATION_LIFECYCLE_DOCUMENTS')
      )
    );
  }

  get applicationApprovalAssets() {
    // approval Assets for TYPE 3 programs are stored at the application level
    return Object.freeze(
      this.tasAssets.filter(
        (asset) =>
          !asset.attachmentDeletedAt &&
          (asset.classification === 'COURSE_APPROVAL_DOCUMENTS' ||
            asset.classification === 'APPLICATION_LIFECYCLE_DOCUMENTS')
      )
    );
  }

  get typeClassification() {
    return this.tasProgramInstance?.tasProgramTemplate?.typeClassification || '';
  }

  get requiresGPA() {
    const allowableType =
      this.typeClassification === 'TAS.ProgramType.4.2' ||
      this.typeClassification === 'TAS.ProgramType.2';
    return allowableType && this.tasProgramInstance?.tasProgramTemplate?.calculateTotalByGPA;
  }

  get hasRequiredEvidenceAttachments() {
    if (!this.isEvidenceRequired) {
      return true;
    }

    const hasApplicationEvidence = !!this.evidenceAssets.length;

    const allCoursesHaveEvidence = this.activeCourses.every(
      (course) => !!course.evidenceAssets.length
    );

    // If type 2/3, check for evidence at the application level or for all courses
    // doing this to make it backwards compatible with legacy programs
    return allCoursesHaveEvidence || Boolean(this.typeClassification && hasApplicationEvidence);
  }

  get hasPreApprovalAttachments() {
    return !!this.applicationApprovalAssets?.length;
  }

  get isMissingRequiredEvidence() {
    return !this.hasRequiredEvidenceAttachments;
  }

  get hasRequiredPreApprovalAttachments() {
    if (!this.isPreApprovalAttachmentsRequired) {
      return true;
    }

    const hasApplicationApprovalAssets = !!this.applicationApprovalAssets.length;

    const allCoursesHaveApprovalAssets = this.activeCourses.every(
      (course) => !!course.approvalAssets.length
    );

    // If type 2/3/4, check for evidence at the application level or for all courses
    // doing this to make it backwards compatible with legacy programs
    return this.typeClassification ? hasApplicationApprovalAssets : allCoursesHaveApprovalAssets;
  }

  get displayNameOrSemesterCode() {
    const applicationName = getSingleValueForTasField('APPLICATION_NAME', this.fields) as string;

    if (this.semesterCodeIsVisible && this.semesterCode) {
      if (applicationName) {
        return `${this.semesterCode} - ${applicationName}`;
      }
      return this.semesterCode;
    }

    if (applicationName) {
      return applicationName;
    }

    return formatCreatedAtMonthYear(this.createdAt);
  }

  get latestStateChangeLabel() {
    const latestTransition =
      this.transitionLog?.length > 0 ? this.transitionLog[this.transitionLog.length - 1] : null;
    return latestTransition?.to || '';
  }

  get latestStateChangeDate() {
    const latestTransition =
      this.transitionLog?.length > 0 ? this.transitionLog[this.transitionLog.length - 1] : null;

    // logic to handle multiple approvers required so that the latest state change date matches the submitted for course approval date if it's still undergoing the approval process
    if (
      latestTransition?.from === 'TAS.ApplicationState.COURSES_APPROVED' &&
      latestTransition?.to === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL'
    ) {
      return this.applicationSubmittedDate;
    }

    return latestTransition?.timestamp || '';
  }

  get latestStateChangePerson() {
    const latestTransition =
      this.transitionLog?.length > 0 ? this.transitionLog[this.transitionLog.length - 1] : null;
    return latestTransition?.reason?.by?.name || '';
  }

  get applicationSubmittedDate() {
    const pendingState = firstTimeSubmissionMap[this.typeClassification];
    const transition = this.transitionLog.find((transition) => transition.to === pendingState);

    return transition?.timestamp || '';
  }

  get approvedDate() {
    const pendingState = firstApprovalDateMap[this.typeClassification];
    const transition = this.transitionLog.find((transition) => transition.to === pendingState);

    return transition?.timestamp || '';
  }

  get requestedTotalAllCourses() {
    const sum = this.activeCourses
      .map((course) => course.courseTotal)
      .reduce((a, b) => {
        return a + b;
      }, 0);
    // This is in pennies, we should never have a decimal.
    // Rounding this here in case migrated data has decimals.
    return Math.round(sum);
  }

  get requestedTotal() {
    const totalAll = this.requestedTotalAllCourses;
    if (totalAll === 0) return 0;

    const baseTotal = Math.max(0, totalAll - this.scholarshipsTotal);

    const template = this.tasProgramInstance?.tasProgramTemplate;
    if (template?.calculateTotalByPercent) {
      const percent = template.reimbursementPercentage ?? 100;
      return Math.round((baseTotal * percent) / 100);
    }

    return Math.round(baseTotal);
  }

  /**
   * Remaining CourseCa$h balance in cents.
   *
   * `amounts.course_cash.reserved` IS the remaining balance — the backend has
   * already netted out used and refunded funds (see the contract comment in
   * course-cash/stripe-card-display.gts and disbursement.ts's module doc). So
   * this returns `reserved` directly. Subtracting `used` here would double-count
   * the spend: a $5.00 allocation with $2.00 spent arrives as
   * `{ reserved: 300, used: 200 }` and must read $3.00, not $1.00.
   *
   * There is no field carrying the ORIGINAL allocation — that is reconstructed
   * as `used + reserved` (disbursement.ts:187).
   */
  get courseCashAvailableBalance() {
    const reserved = this.amounts?.course_cash?.reserved ?? 0;
    return Math.max(0, reserved);
  }

  get requestedCourseCashSubtotal() {
    const sum = this.activeCourses
      .map((course) => course.courseTuition)
      .reduce((a, b) => {
        return a + b;
      }, 0);
    // This is in pennies, we should never have a decimal.
    // Rounding this here in case migrated data has decimals.
    return Math.round(sum);
  }

  get requestedCourseCashSubtotalLessScholarships() {
    const subtotal = Math.max(0, this.requestedCourseCashSubtotal - this.scholarshipsTotal);

    const template = this.tasProgramInstance?.tasProgramTemplate;
    if (template?.calculateTotalByPercent) {
      const percent = template.reimbursementPercentage ?? 100;
      return Math.round((subtotal * percent) / 100);
    }

    return Math.round(subtotal);
  }

  get requestedNonCourseCashSubtotal() {
    const sum = this.activeCourses
      .map((course) => course.nonTuitionExpenses)
      .reduce((a, b) => {
        return a + b;
      }, 0);
    // This is in pennies, we should never have a decimal.
    // Rounding this here in case migrated data has decimals.
    const subtotal = Math.round(sum);

    const template = this.tasProgramInstance?.tasProgramTemplate;
    if (template?.calculateTotalByPercent) {
      const percent = template.reimbursementPercentage ?? 100;
      return Math.round((subtotal * percent) / 100);
    }

    return subtotal;
  }

  get requestedBooksSubtotal() {
    const sum = this.activeCourses.map((course) => course.courseBooks).reduce((a, b) => a + b, 0);
    return Math.round(sum);
  }

  get requestedLabsSubtotal() {
    const sum = this.activeCourses.map((course) => course.courseLabs).reduce((a, b) => a + b, 0);
    return Math.round(sum);
  }

  get requestedSoftwareSubtotal() {
    const sum = this.activeCourses
      .map((course) => course.courseSoftware)
      .reduce((a, b) => a + b, 0);
    return Math.round(sum);
  }

  get requestedFeesSubtotal() {
    const sum = this.activeCourses.map((course) => course.courseFees).reduce((a, b) => a + b, 0);
    return Math.round(sum);
  }

  get scholarships() {
    return this.fields?.['SCHOLARSHIPS_RECEIVED']?.values || [];
  }

  get scholarshipsTotal() {
    const sum = this.scholarships.reduce((sum: number, scholarship: ReceivedScholarship) => {
      return sum + (scholarship.scholarshipAmount || 0);
    }, 0);
    // This is in pennies, we should never have a decimal.
    // Rounding this here in case migrated data has decimals.
    return Math.round(sum);
  }

  get includesScholarships() {
    return this.scholarshipsTotal > 0;
  }

  get approvedTotal() {
    return (getSingleValueForTasField('FINAL_APPROVED_TOTAL', this.fields) ??
      getSingleValueForTasField('PRE_APPROVED_TOTAL', this.fields)) as number | undefined;
  }

  /** The amount approved at the courses-preapproval step, on its own. */
  get preApprovedTotal() {
    return getSingleValueForTasField('PRE_APPROVED_TOTAL', this.fields) as number | undefined;
  }

  /**
   * FINAL_APPROVED_TOTAL only — unlike `approvedTotal`, which coalesces to
   * PRE_APPROVED_TOTAL and therefore changes meaning between approval stages.
   * Caution: the backend mirrors PRE into FINAL on every save until final
   * approval overwrites it (TasApplication#sync_approved_totals_if_applicable),
   * so this is NOT empty before final approval — check the application state
   * to know whether final approval has actually happened.
   */
  get finalApprovedTotal() {
    return getSingleValueForTasField('FINAL_APPROVED_TOTAL', this.fields) as number | undefined;
  }

  getTotalByState(state: string): number {
    return this.tasAssistances.reduce(
      (total, assistance) => (assistance.state === state ? total + assistance.amount : total),
      0
    );
  }

  get isPayable() {
    return this.tasAssistances.some(
      (assistance) => assistance.state === 'TAS.AssistanceState.PAYABLE'
    );
  }

  get paidTotal() {
    return this.getTotalByState('TAS.AssistanceState.SETTLED');
  }

  get forgivenTotal() {
    return this.getTotalByState('TAS.AssistanceState.FORGIVEN');
  }

  get paidAndForgivenTotal() {
    return this.paidTotal + this.forgivenTotal;
  }

  get paidDate() {
    let earliestDate: string | null = null;

    this.tasAssistances.forEach((assistance: TasAssistanceModel) => {
      const settledDate = assistance.settledDate;

      if (settledDate && (!earliestDate || settledDate < earliestDate)) {
        earliestDate = settledDate;
      }
    });

    return earliestDate;
  }

  // Typed as a literal so consumers holding a
  // `TasApplication | TASProgramInstanceModel` union can narrow via
  // `app.modelName === 'tas-application'`.
  get modelName(): 'tas-application' {
    return 'tas-application';
  }

  get currentExpectedApproverEmployeeIds() {
    const approvalConfigType =
      this.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL'
        ? 'evidenceApproval'
        : 'coursesApproval';
    const reviewers: ApplicationApprovalSummaryApprover[] =
      this[approvalConfigType]?.current_reviewers || [];
    return new Set(
      reviewers.map((reviewer) => reviewer['employee_id']).filter((id): id is number => id != null)
    );
  }

  get allPossibleApproverEmployeeIdsForAllWorkflows() {
    const evidenceReviewers = this.evidenceApproval.possible_reviewers || [];
    const courseReviewers = this.coursesApproval.possible_reviewers || [];
    return new Set(
      [...evidenceReviewers, ...courseReviewers]
        .map((reviewer) => reviewer['employee_id'])
        .filter((id): id is number => id != null)
    );
  }

  /**
   * Employee ids listed anywhere in the application's course or evidence approval workflows.
   * This includes prior, current, possible, and required reviewers so detail access is not
   * confused with whether the employee is expected to act on the current step.
   *
   * @returns the employee ids assigned to any approval workflow on the application
   */
  get allApproverEmployeeIdsForAllWorkflows() {
    const approvals = [this.coursesApproval, this.evidenceApproval];
    const reviewers = approvals.flatMap((approval) => [
      ...(approval?.affirmed || []),
      ...(approval?.current_reviewers || []),
      ...(approval?.possible_reviewers || []),
      ...(approval?.required || []),
    ]);
    return new Set(
      reviewers.map((reviewer) => reviewer.employee_id).filter((id): id is number => id != null)
    );
  }

  get isRefundRequested() {
    return this.paymentState === 'TAS.ApplicationPaymentState.REFUND_REQUESTED';
  }

  get amountOwed() {
    let amount = 0;
    for (const assistance of this.tasAssistances) {
      if (assistance.state === 'TAS.AssistanceState.REPAYABLE') {
        amount += Math.abs(assistance.amount);
      }
    }
    return amount;
  }

  get repaymentAmountOutstanding() {
    return this.paidTotal - this.amountOwed;
  }

  get isInCollections() {
    return (this.tags || []).some((tag) => tag.name === 'In Collections');
  }

  get shouldDisplayApprovedTotal() {
    // Only display approved total if the application is in state ATTEND and up
    return !this.inPreAttendState;
  }

  get inPreAttendState() {
    return this.preAttendStates.has(this.state);
  }

  get inAttendOrPriorState() {
    return this.preAttendAndAttendStates.has(this.state);
  }

  get shouldDisplayPaymentDetails() {
    const amounts = this.amounts;
    if (!amounts) return false;

    const hasPaymentFlags =
      amounts.flags?.request_payment_enabled || amounts.flags?.request_repayment_enabled;
    const hasPayments = (amounts.paid ?? 0) > 0 || (amounts.pending_payment ?? 0) > 0;
    const hasCourseCash =
      (amounts.course_cash?.reserved ?? 0) > 0 ||
      (amounts.course_cash?.used ?? 0) > 0 ||
      (amounts.course_cash?.refunded ?? 0) > 0;

    return hasPaymentFlags || hasPayments || hasCourseCash;
  }

  get isDormantState() {
    return this.dormantStates.has(this.state);
  }

  get certificateApplicationHasSubmitted() {
    return (
      this.tasProgramInstance?.tasProgramTemplate?.isCertificateProgram &&
      this.hasSubmittedApplicationStates.has(this.state)
    );
  }

  get applicationHasSubmittedForEvidenceApproval() {
    return this.postSubmitForPaymentStates.has(this.state);
  }

  get inRescindableState() {
    return this.rescindableStates.has(this.state);
  }

  // If the application allows for post-approval edits to approved total.
  get isEditableApprovedTotal() {
    const isUneditable =
      this.uneditableApprovedTotalPaymentStates.has(this.paymentState) ||
      this.uneditableApprovedTotalApplicationStates.has(this.state);
    return !isUneditable;
  }

  // True when the application has at least one reimbursement assistance (any state).
  // Used to gate the in-drawer "Approved Reimbursement" editor so it never renders a
  // $0 reimbursement on a pure Course Cash application.
  get hasReimbursementAssistance() {
    return this.tasAssistances.some(
      (assistance) => assistance.manner === 'TAS.AssistanceManner.REIMBURSEMENT'
    );
  }

  // Mirrors the tio-api `contributes_to_reimbursement_limit_ledger_amount` scope
  // (app/models/tas_assistance.rb): a REIMBURSEMENT assistance in a state that
  // contributes to the ledger — i.e. NOT in {CREATED, ABANDONED, UNSETTLED}. When
  // true the application is on the "assistance path" (editing the approved total
  // creates payment/repayment deltas); when false it is on the "field path" where
  // editing FINAL_APPROVED_TOTAL reconciles the UNSETTLED reimbursement row.
  get hasLedgerContributingReimbursement() {
    return this.tasAssistances.some(
      (assistance) =>
        assistance.manner === 'TAS.AssistanceManner.REIMBURSEMENT' &&
        assistance.state !== 'TAS.AssistanceState.CREATED' &&
        assistance.state !== 'TAS.AssistanceState.ABANDONED' &&
        assistance.state !== 'TAS.AssistanceState.UNSETTLED'
    );
  }

  // True when the application has a Course Cash assistance (any state/amount). Used to keep the
  // payment-lifecycle (request summary) drawer view for a Course Cash app even when its reservation
  // has been reduced to $0, instead of collapsing back to the standard approval summary.
  get hasCourseCashAssistance() {
    return this.tasAssistances.some(
      (assistance) => assistance.manner === 'TAS.AssistanceManner.COURSE_CASH'
    );
  }

  // Whether the application has any Course Cash that has been spent via card.
  // Course Cash usage does not move the reimbursement paymentState, so it must be
  // considered independently when deciding whether funds can be clawed back.
  get hasCourseCashUsed() {
    return (this.amounts?.course_cash?.used ?? 0) > 0;
  }

  // If the application allows for request repayment (clawback).
  get canRequestRepayment() {
    return this.clawbackablePaymentStates.has(this.paymentState);
  }

  // Reject-time recovery includes Course Cash spend even when reimbursement paymentState never changed.
  get canRepayOnReject() {
    return this.canRequestRepayment || this.hasCourseCashUsed;
  }

  get latestPaymentDate() {
    // TODO: We should be looking at assistance always. This is a bug.
    return this.paymentStateLog.at(-1)?.reason?.payment?.date;
  }

  // Guarded like every other read of this chain in this file. The unguarded version threw
  // `Cannot read properties of null (reading 'semesterCodeIsVisible')` for the same reason
  // the limits summary did (Datadog issue da24e4ac); it survived only because semester code
  // is not rendered on the approval page. The guard is against the link being UNREACHABLE in
  // the store, not against a program without a template — both FKs in this chain are NOT
  // NULL, so that cannot exist. Its one consumer (`displayNameOrSemesterCode`) treats this as
  // a boolean, so undefined behaves as "not visible".
  get semesterCodeIsVisible() {
    return this.tasProgramInstance?.tasProgramTemplate?.semesterCodeIsVisible;
  }

  get semesterCode() {
    return getSingleValueForTasField('SEMESTER_CODE', this.fields);
  }

  get totalCredits() {
    return this.activeCourses.reduce((total, course) => total + course.courseCredit, 0);
  }

  /**
   * Whether this application's program uses credits-based limits
   * instead of monetary limits.
   */
  get isCreditBased(): boolean {
    const limitCapacity = getSingleValueForTasField(
      'LIMIT_CAPACITY',
      this.tasProgramInstance?.tasProgramTemplate?.fields
    );
    return limitCapacity === 'TAS.LimitCapacity.CREDITS';
  }

  get totalPassingCredits() {
    return this.activeCourses.reduce((total, course) => total + course.passingCredit, 0);
  }

  get fractionalPercentPassed(): number {
    if (this.totalCredits === 0) return 0;
    // If the application is in the PENDING_COURSES_APPROVAL state, the fractional percent
    // passed should be 1. This is because the course has to be completed before a grade
    // can be assigned and a true fractional percent passed can be calculated.
    if (this.state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL') {
      return 1;
    }
    const rawPercentage = this.totalPassingCredits / this.totalCredits;
    return parseFloat(rawPercentage.toFixed(2));
  }

  // Ensure we have a properly initialized TasClaimsFinancials object when reading data.
  // `fractionPercentPassed` is a dynamic value throughout the lifecycle of the application
  // so any new calculations must be made with the most current value.
  get claimsFinancialsCalculatorInput(): Readonly<TasClaimsFinancials> {
    const defaultCF = createDefaultClaimsFinancialsWithBenefitPercent(
      this.tasProgramInstance?.tasProgramTemplate?.fractionalBenefitPercentForClaimsFinancials,
      this.requestedTotal
    );
    const currentCF = this.fields['CLAIMS_FINANCIALS'].values[0] || {};
    return {
      ...defaultCF,
      ...currentCF,
      CF_FRACTIONAL_PERCENT_PASSED: this.fractionalPercentPassed,
    };
  }

  get assistanceHistory() {
    return this.history?.assistance_history || [];
  }

  get paymentsHistory() {
    return this.assistanceHistory.flatMap((group: AssistanceAppHistory) => group.records ?? []);
  }

  get approvedTotalHistory() {
    const appHistory = this.history?.application_history || [];
    return appHistory.filter((item: AppHistoryBase) => item.logType === 'approval');
  }

  get fullHistory() {
    const appHistory = this.history?.application_history ?? [];
    return [...appHistory, ...this.paymentsHistory];
  }

  get inRepayment() {
    // Check if there's still an outstanding refund balance
    const refundRequested = this.amounts?.refund_requested ?? 0;
    const refundPaidBack = this.amounts?.refund_paid_back ?? 0;
    const refundForgiven = this.amounts?.refund_forgiven ?? 0;
    const refundCancelled = this.amounts?.refund_cancelled ?? 0;

    const outstandingBalance =
      refundRequested - (refundPaidBack + refundForgiven + refundCancelled);
    return outstandingBalance > 0;
  }

  get fromState(): TasApplicationState {
    return (this.transitionLog?.at(-1)?.from as TasApplicationState) || '';
  }

  get fromPaymentState(): TasApplicationPaymentState {
    return this.paymentStateLog?.at(-1)?.from as TasApplicationPaymentState;
  }

  get badgeIntent() {
    // Prioritize paymentState if in active repayment (outstanding balance > 0)
    // Otherwise fall back to application state
    let stateToFormat: TasApplicationState | TasApplicationPaymentState = this.state;
    if (this.inRepayment) {
      // Always show REFUND_REQUESTED intent when in repayment
      stateToFormat = 'TAS.ApplicationPaymentState.REFUND_REQUESTED';
    }
    const formattedState = formatStateKey(stateToFormat, this.fromState, this.typeClassification);
    return tasIntentMap[formattedState] || 'default';
  }

  get badgeLabel() {
    // Prioritize paymentState if in active repayment (outstanding balance > 0)
    // Otherwise fall back to application state
    let stateToFormat: TasApplicationState | TasApplicationPaymentState = this.state;
    if (this.inRepayment) {
      // Use paymentState if available, otherwise default to REFUND_REQUESTED
      stateToFormat = 'TAS.ApplicationPaymentState.REFUND_REQUESTED';
    }
    const formattedState = formatStateKey(stateToFormat, this.fromState, this.typeClassification);
    return tasStateLabelMap[formattedState] || '';
  }

  /** Translation key for `badgeLabel`; empty when the state has no label. */
  get badgeLabelKey() {
    const stateToFormat: TasApplicationState | TasApplicationPaymentState = this.inRepayment
      ? 'TAS.ApplicationPaymentState.REFUND_REQUESTED'
      : this.state;
    const formattedState = formatStateKey(stateToFormat, this.fromState, this.typeClassification);
    return tasStateLabelKeyMap[formattedState] || '';
  }

  get hasWaiverApplied() {
    const attachmentWaived = getSingleValueForTasField(
      'WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT',
      this.fields
    );
    const approvalWaived = getSingleValueForTasField(
      'WAIVE_EVIDENCE_APPROVAL_REQUIREMENT',
      this.fields
    );
    return attachmentWaived === true || approvalWaived === true;
  }
}

const Registration = {
  kind: 'object',
  name: 'tas-application',
  features: TasApplicationExtension,
};
export default Registration;
